// src/modules/jobs/JobsDetail.tsx
import { useState, useMemo, useEffect, useCallback } from "react";
import { useParams } from "react-router";
import { useSearchParams } from "react-router-dom";
import ResizableSplitView from "@components/ResizableSplitView/ResizableSplitView";
import type { TreeNode } from "@components/TreeNavigator/TreeNavigator";
import Loader from "../../components/common/loader/loader";
import useJobDetail from "./hooks/useJobDetail";
import JobTreeSidebar, { collectAllJobIds } from "./components/JobTreeSidebar";
import JobDetailPanel from "./components/JobDetailPanel";
import { Job } from "@models/job.model";
import { useDocumentTitle } from "@/shared/hooks/useDocumentTitle";

import "./styles.scss";

/** Recursively find a Job by id in the tree. */
function findJobById(job: Job, id: number): Job | null {
    if (job.id === id) return job;
    for (const child of job.children ?? []) {
        const found = findJobById(child, id);
        if (found) return found;
    }
    return null;
}

export default function JobDetail() {
    const { jobId } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const { isLoading, job: rootJob, reload } = useJobDetail(Number(jobId));

    const allIds = useMemo(() => rootJob ? collectAllJobIds(rootJob) : new Set<string>(), [rootJob]);
    const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set<string>());
    /* Which child of the tree is open. In the URL rather than in state so that
       following a fix link out to an engagement and coming back -- by the browser's
       back button or by the link the destination offers -- lands on the same job,
       not on the root of its batch. */
    const selectedJobId = searchParams.get("selected") ?? jobId ?? "";

    // Expand all nodes when job tree loads/refreshes, but preserve selection
    useEffect(() => {
        if (allIds.size > 0) {
            setExpandedIds(new Set(allIds));
        }
    }, [allIds]);

    const selectedJob = useMemo(() => {
        if (!rootJob) return null;
        return findJobById(rootJob, Number(selectedJobId)) ?? rootJob;
    }, [rootJob, selectedJobId]);

    useDocumentTitle(
        selectedJob?.name ?? (rootJob ? `Job #${rootJob.id}` : "Job")
    );

    const handleSelect = useCallback((node: TreeNode) => {
        setSearchParams((prev: URLSearchParams) => {
            const next = new URLSearchParams(prev);
            // The root needs no marker; leaving it out keeps the common URL clean.
            if (node.id === jobId) next.delete("selected");
            else next.set("selected", node.id);
            return next;
        }, { replace: true });
    }, [jobId, setSearchParams]);

    const handleToggle = useCallback((nodeId: string) => {
        setExpandedIds((prev) => {
            const next = new Set(prev);
            if (next.has(nodeId)) {
                next.delete(nodeId);
            } else {
                next.add(nodeId);
            }
            return next;
        });
    }, []);

    if (isLoading) {
        return <div className="job-detail-page"><Loader /></div>;
    }

    if (!rootJob) {
        return <div className="job-detail-page"><div className="text-muted text-center p-5">Job not found</div></div>;
    }

    return (
        <div className="job-detail-page">
            <ResizableSplitView
                storageKey="jobs-detail.leftPanel.width"
                defaultLeftSize={300}
                minLeftSize={220}
                minRightSize={400}
                left={
                    <JobTreeSidebar
                        rootJob={rootJob}
                        selectedJobId={selectedJobId}
                        expandedIds={expandedIds}
                        onSelect={handleSelect}
                        onToggle={handleToggle}
                        onRefresh={reload}
                    />
                }
                right={
                    selectedJob
                        ? <JobDetailPanel key={selectedJobId} job={selectedJob} />
                        : <div className="text-muted text-center p-5">Select a job from the tree</div>
                }
            />
        </div>
    );
}
